import { useState } from "react";
import { motion } from "framer-motion";

export function VoiceWaveform() {
  const bars = 40;
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-8 overflow-hidden" data-testid="voice-waveform">
      {/* Recording header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative">
          <div className="w-3 h-3 rounded-full bg-rogue-rose" />
          <motion.div
            className="absolute inset-0 w-3 h-3 rounded-full bg-rogue-rose"
            animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
        </div>
        <span className="text-xs font-mono text-rogue-rose">Recording · Cerebrum Voice</span>
        <span className="ml-auto text-xs font-mono text-geminii-dim">00:04.7</span>
      </div>

      {/* Waveform */}
      <div className="flex items-center justify-center gap-[2px] h-16 mb-6">
        {Array.from({ length: bars }, (_, i) => {
          const height = Math.sin(i * 0.4) * 30 + Math.random() * 20 + 10;
          return (
            <motion.div
              key={i}
              className="w-1 rounded-full bg-rogue-cyan"
              initial={{ height: 4 }}
              animate={{ height: [height * 0.3, height, height * 0.5, height * 0.8, height * 0.4] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.03,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      {/* Transcription */}
      <div className="border-t border-white/[0.06] pt-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-1.5 rounded-full bg-rogue-emerald" />
          <span className="text-[10px] font-mono text-rogue-emerald uppercase tracking-wider">Live Transcription</span>
        </div>
        <p className="text-sm text-white leading-relaxed">
          <span>"The pump at site 14 is showing pressure drops again. </span>
          <span className="text-geminii-muted">I think the valve seal needs replacing — </span>
          <motion.span
            className="text-rogue-cyan"
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          >
            same issue we had in March…
          </motion.span>
          <motion.span
            className="inline-block w-[2px] h-4 bg-rogue-cyan ml-0.5 align-middle"
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 0.6, repeat: Infinity }}
          />
        </p>
        <p className="text-xs text-geminii-dim mt-3 italic">
          Cerebrum auto-structures this into a JC note · ReadAloudButton plays it back via TTS.
        </p>
      </div>
    </div>
  );
}

export function AIInsightCard() {
  return (
    <div className="bg-geminii-card border border-cerebrum-violet/20 rounded-2xl p-8 overflow-hidden" data-testid="ai-insight-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-cerebrum-violet/20 flex items-center justify-center">
            <motion.div
              className="w-2 h-2 rounded-full bg-cerebrum-violet"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          <span className="text-xs font-mono text-cerebrum-violet">Cerebrum · Daily Brief</span>
        </div>
        <span className="text-[10px] font-mono text-geminii-dim">via Notifications Bell · 3 new</span>
      </div>
      <div className="space-y-3">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex items-start gap-3 p-3 rounded-lg bg-cerebrum-violet/5 border border-cerebrum-violet/10"
        >
          <div className="w-1 h-full min-h-[40px] rounded-full bg-cerebrum-violet/40 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-white mb-1">Revenue alert</p>
            <p className="text-xs text-geminii-muted">Customer <span className="text-white">Acacia Farms</span> revenue down 34% this quarter. Last ticket unresolved for 12 days. Consider priority outreach.</p>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex items-start gap-3 p-3 rounded-lg bg-rogue-emerald/5 border border-rogue-emerald/10"
        >
          <div className="w-1 h-full min-h-[40px] rounded-full bg-rogue-emerald/40 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-white mb-1">Project margin</p>
            <p className="text-xs text-geminii-muted">Project <span className="text-white">Solar Array B</span> tracking at <span className="text-rogue-emerald">42% margin</span> — 8% above budget. Procurement savings driving the delta.</p>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="flex items-start gap-3 p-3 rounded-lg bg-rogue-amber/5 border border-rogue-amber/10"
        >
          <div className="w-1 h-full min-h-[40px] rounded-full bg-rogue-amber/40 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-white mb-1">Slippage detected</p>
            <p className="text-xs text-geminii-muted">3 tasks on <span className="text-white">Water Treatment Phase 2</span> are 5+ days behind baseline. <span className="text-rogue-amber">Critical path at risk.</span></p>
          </div>
        </motion.div>
      </div>
      <p className="text-[10px] text-geminii-dim mt-4 italic">
        Rogi (your default Cerebrum persona) writes every brief — thumbs-down once and it rewrites in your voice.
      </p>
    </div>
  );
}

export function ModuleFlowDiagram() {
  const nodes = [
    { label: "Suspect", color: "bg-geminii-dim/30", textColor: "text-geminii-muted" },
    { label: "Lead", color: "bg-rogue-cyan/20", textColor: "text-rogue-cyan" },
    { label: "Customer", color: "bg-rogue-emerald/20", textColor: "text-rogue-emerald" },
    { label: "Job Card", color: "bg-rogue-amber/20", textColor: "text-rogue-amber" },
    { label: "Invoice", color: "bg-cerebrum-violet/20", textColor: "text-cerebrum-violet" },
  ];
  
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-8" data-testid="module-flow">
      <p className="text-xs font-mono text-geminii-dim mb-6 uppercase tracking-wider">Data flows across modules</p>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {nodes.map((node, i) => (
          <div key={node.label} className="flex items-center gap-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className={`px-4 py-2 rounded-lg ${node.color} border border-white/[0.06]`}
            >
              <span className={`text-xs font-medium ${node.textColor}`}>{node.label}</span>
            </motion.div>
            {i < nodes.length - 1 && (
              <motion.svg
                width="20" height="10" viewBox="0 0 20 10"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.4 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 + 0.1 }}
              >
                <path d="M0 5 L15 5 L12 2 M15 5 L12 8" stroke="currentColor" fill="none" strokeWidth="1" className="text-geminii-dim" />
              </motion.svg>
            )}
          </div>
        ))}
      </div>
      <p className="text-center text-[10px] text-geminii-dim mt-4">Cerebrum AI connects every stage</p>
    </div>
  );
}

export function LiveDashboardPulse() {
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-8 overflow-hidden" data-testid="live-pulse">
      <div className="flex items-center gap-2 mb-5">
        <motion.div
          className="w-2 h-2 rounded-full bg-rogue-emerald"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <span className="text-xs font-mono text-rogue-emerald">Dashboard · Live</span>
        <span className="text-[10px] font-mono text-geminii-dim ml-auto">rogue.geminii-ai.com</span>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Upcoming jobs", value: "37", color: "text-rogue-cyan" },
          { label: "Open leads", value: "12", color: "text-rogue-emerald" },
          { label: "Unresolved tickets", value: "8", color: "text-rogue-amber" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.15 }}
            className="text-center"
          >
            <motion.p
              className={`font-display font-bold text-2xl ${stat.color}`}
              animate={{ opacity: [1, 0.7, 1] }}
              transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
            >
              {stat.value}
            </motion.p>
            <p className="text-[10px] text-geminii-dim">{stat.label}</p>
          </motion.div>
        ))}
      </div>
      {/* Activity feed — mirrors NotificationsBell stream */}
      <div className="space-y-2">
        {[
          { action: "JC #A1B2C3 completed", who: "Marcus T.", time: "2m ago", color: "bg-rogue-emerald" },
          { action: "Lead from web form", who: "SalesFlow", time: "5m ago", color: "bg-rogue-cyan" },
          { action: "Ticket #T-392 assigned", who: "Cerebrum auto-route", time: "8m ago", color: "bg-cerebrum-violet" },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 + i * 0.2 }}
            className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02]"
          >
            <div className={`w-1.5 h-1.5 rounded-full ${item.color} flex-shrink-0`} />
            <span className="text-xs text-white flex-1">{item.action}</span>
            <span className="text-[10px] text-geminii-dim">{item.who}</span>
            <span className="text-[10px] text-geminii-dim">{item.time}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function GPSTrackingVisual() {
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-8 overflow-hidden relative" data-testid="gps-visual">
      <div className="flex items-center justify-between gap-2 mb-5">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-rogue-emerald"
            animate={{ scale: [1, 1.4, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
          <span className="text-xs font-mono text-rogue-emerald uppercase tracking-wider">Field Map · Live</span>
        </div>
        <span className="text-[10px] font-mono text-geminii-dim">GPS-verified clock-in · Plus Codes</span>
      </div>
      {/* Simulated map grid */}
      <div className="relative h-48 rounded-lg bg-geminii-deep border border-white/[0.04] overflow-hidden">
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        {/* Animated dots representing field techs */}
        {[
          { x: "20%", y: "30%", name: "Marcus T.", jc: "#A1B2C3", color: "bg-rogue-emerald" },
          { x: "55%", y: "60%", name: "Johan V.", jc: "#D4E5F6", color: "bg-rogue-cyan" },
          { x: "75%", y: "25%", name: "Sarah K.", jc: "#G7H8I9", color: "bg-rogue-amber" },
          { x: "40%", y: "70%", name: "Pieter L.", jc: "#J0K1L2", color: "bg-cerebrum-violet" },
        ].map((tech, i) => (
          <motion.div
            key={tech.name}
            className="absolute"
            style={{ left: tech.x, top: tech.y }}
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 + i * 0.2, type: "spring" }}
          >
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
            >
              <div className={`w-3 h-3 rounded-full ${tech.color} shadow-lg`} />
              <div className={`absolute inset-0 w-3 h-3 rounded-full ${tech.color} animate-ping opacity-30`} />
            </motion.div>
            <div className="absolute top-4 left-4 whitespace-nowrap">
              <p className="text-[9px] text-white">{tech.name}</p>
              <p className="text-[8px] font-mono text-rogue-cyan/70">{tech.jc}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <span className="text-xs text-geminii-muted">4 technicians on active JCs</span>
        <span className="text-[10px] text-rogue-emerald">All clock-ins GPS-verified</span>
      </div>
    </div>
  );
}

export function OCRScanVisual() {
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-8 overflow-hidden" data-testid="ocr-visual">
      <div className="flex items-center justify-between gap-2 mb-5">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-cerebrum-violet"
            animate={{ scale: [1, 1.4, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
          <span className="text-xs font-mono text-cerebrum-violet uppercase tracking-wider">Documents Hub · OCR</span>
        </div>
        <span className="text-[10px] font-mono text-geminii-dim">Cerebrum Vision · async</span>
      </div>
      <div className="flex gap-6 items-start">
        {/* "Receipt" */}
        <div className="w-36 bg-white/[0.03] rounded-lg p-4 border border-white/[0.06] flex-shrink-0">
          <div className="space-y-2">
            <div className="h-2 bg-white/[0.08] rounded w-full" />
            <div className="h-2 bg-white/[0.06] rounded w-3/4" />
            <div className="h-px bg-white/[0.04] my-3" />
            <div className="h-2 bg-white/[0.06] rounded w-full" />
            <div className="h-2 bg-white/[0.06] rounded w-2/3" />
            <div className="h-2 bg-white/[0.06] rounded w-5/6" />
            <div className="h-px bg-white/[0.04] my-3" />
            <div className="h-2 bg-white/[0.08] rounded w-1/2 ml-auto" />
          </div>
          {/* Scan line */}
          <motion.div
            className="w-full h-0.5 bg-rogue-cyan/60 rounded mt-2"
            animate={{ y: [-60, 60] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
        </div>
        {/* Extracted data */}
        <div className="flex-1 space-y-2">
          {[
            { field: "Status", value: "processing → ready (24s)", delay: 0.3 },
            { field: "Vendor", value: "FastParts Industrial (Pty) Ltd", delay: 0.5 },
            { field: "Invoice #", value: "INV-2024-0847", delay: 0.7 },
            { field: "Date", value: "14/02/2026", delay: 0.9 },
            { field: "Lines", value: "3 line items · fuzzy-matched", delay: 1.1 },
            { field: "Total", value: "R 1,247.50 (excl. VAT)", delay: 1.3 },
          ].map((item) => (
            <motion.div
              key={item.field}
              initial={{ opacity: 0, x: 10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: item.delay }}
              className="flex items-center gap-2"
            >
              <span className="text-[10px] text-geminii-dim w-16">{item.field}</span>
              <span className="text-xs text-white font-mono">{item.value}</span>
            </motion.div>
          ))}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.6 }}
            className="text-[10px] text-rogue-emerald mt-3 pt-2 border-t border-white/[0.06]"
          >
            Matched to supplier · FastParts Industrial (98% confidence) · bell notification fired
          </motion.p>
        </div>
      </div>
    </div>
  );
}


/* ---------------------------------------------------------------- */
/* Projects — Animated Gantt Chart                                  */
/* ---------------------------------------------------------------- */
export function GanttChartVisual() {
  const today = 58; // % across timeline
  const tasks = [
    { name: "Site survey & scoping", start: 4, span: 14, color: "bg-rogue-cyan", progress: 100, owner: "Sarah K." },
    { name: "Procurement — long-lead items", start: 12, span: 20, color: "bg-rogue-amber", progress: 90, owner: "Stockroom" },
    { name: "Civils & foundations", start: 22, span: 22, color: "bg-rogue-emerald", progress: 75, owner: "Field crew A" },
    { name: "Mechanical install", start: 38, span: 26, color: "bg-cerebrum-violet", progress: 35, owner: "Field crew B", critical: true },
    { name: "Commissioning & QA", start: 58, span: 18, color: "bg-rogue-cyan", progress: 5, owner: "Marcus T." },
    { name: "Handover & client sign-off", start: 74, span: 14, color: "bg-rogue-rose", progress: 0, owner: "PM" },
  ];
  const weeks = ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10"];

  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8 overflow-hidden" data-testid="gantt-visual">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <motion.div
              className="w-1.5 h-1.5 rounded-full bg-rogue-emerald"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono text-rogue-emerald uppercase tracking-wider">Live Gantt — Solar Array B</span>
          </div>
          <p className="text-xs text-geminii-dim">68% complete · 4 weeks remaining · on baseline</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm bg-cerebrum-violet" />
            <span className="text-[10px] font-mono text-geminii-dim">Critical path</span>
          </div>
        </div>
      </div>

      {/* Week scale */}
      <div className="grid grid-cols-10 gap-px mb-2 text-[9px] font-mono text-geminii-dim border-b border-white/[0.05] pb-2">
        {weeks.map((w) => (
          <span key={w} className="text-center">{w}</span>
        ))}
      </div>

      {/* Tasks */}
      <div className="space-y-2.5 relative">
        {/* "Today" vertical marker */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute top-0 bottom-0 w-px bg-rogue-rose/50 z-10 pointer-events-none"
          style={{ left: `${today}%` }}
        >
          <span className="absolute -top-1 -translate-x-1/2 text-[9px] font-mono text-rogue-rose bg-geminii-card px-1">
            Today
          </span>
        </motion.div>

        {tasks.map((task, i) => (
          <div key={task.name} className="grid grid-cols-[140px_1fr] gap-3 items-center">
            {/* Label */}
            <div className="text-right">
              <p className="text-[11px] text-white truncate">{task.name}</p>
              <p className="text-[9px] text-geminii-dim">{task.owner}</p>
            </div>
            {/* Bar track */}
            <div className="relative h-6 bg-white/[0.025] rounded overflow-hidden">
              {/* Bar */}
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                whileInView={{ width: `${task.span}%`, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.15, duration: 0.6, ease: "easeOut" }}
                className={`absolute top-0 h-full ${task.color} ${task.critical ? "ring-1 ring-cerebrum-violet/50" : ""} rounded-sm flex items-center px-2`}
                style={{ left: `${task.start}%` }}
              >
                {/* Progress fill */}
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${task.progress}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + i * 0.15, duration: 0.7 }}
                  className="absolute top-0 left-0 h-full bg-white/25 rounded-sm"
                />
                <span className="relative text-[9px] font-mono text-white/95 z-10">{task.progress}%</span>
              </motion.div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer insight */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 2 }}
        className="mt-5 pt-4 border-t border-white/[0.06] flex items-start gap-2"
      >
        <div className="w-1 h-full min-h-[28px] rounded-full bg-cerebrum-violet/40 flex-shrink-0" />
        <p className="text-[11px] text-geminii-muted leading-relaxed">
          <span className="text-cerebrum-violet font-medium">Cerebrum: </span>
          Mechanical install is on the critical path. Procurement for inverter brackets delivered 2 days early — buffer is healthy.
        </p>
      </motion.div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Teams — Configurable Leaderboard (mirrors Analytics Hub HBar)     */
/*                                                                   */
/* Real Rogue widget: GET /api/analytics/hub?range={7d|30d|90d|...}  */
/* Renders HBar leaderboards across Sales (rep_leaderboard), Field   */
/* (tech_utilisation, photo_compliance, rating_distribution), etc.   */
/* Business picks the metric + range. This visual shows that.        */
/* ---------------------------------------------------------------- */
export function LeaderboardVisual() {
  const [metric, setMetric] = useState(0);
  const [range, setRange] = useState("30d");

  const metrics = [
    {
      key: "jobs",
      label: "Job cards completed",
      unit: "",
      bars: [
        { name: "Marcus T.", role: "Field Tech", value: 47 },
        { name: "Sarah K.", role: "Site Lead", value: 41 },
        { name: "Johan V.", role: "Mechanic", value: 33 },
        { name: "Pieter L.", role: "Field Tech", value: 28 },
        { name: "Naledi M.", role: "Apprentice", value: 19 },
      ],
    },
    {
      key: "overtime",
      label: "Overtime hours clocked",
      unit: "h",
      bars: [
        { name: "Johan V.", role: "Mechanic", value: 32 },
        { name: "Marcus T.", role: "Field Tech", value: 28 },
        { name: "Pieter L.", role: "Field Tech", value: 21 },
        { name: "Sarah K.", role: "Site Lead", value: 14 },
        { name: "Naledi M.", role: "Apprentice", value: 6 },
      ],
    },
    {
      key: "rating",
      label: "Avg customer rating",
      unit: "★",
      bars: [
        { name: "Sarah K.", role: "Site Lead", value: 4.9 },
        { name: "Marcus T.", role: "Field Tech", value: 4.8 },
        { name: "Naledi M.", role: "Apprentice", value: 4.6 },
        { name: "Johan V.", role: "Mechanic", value: 4.5 },
        { name: "Pieter L.", role: "Field Tech", value: 4.2 },
      ],
    },
    {
      key: "photo",
      label: "Photo compliance %",
      unit: "%",
      bars: [
        { name: "Marcus T.", role: "Field Tech", value: 100 },
        { name: "Sarah K.", role: "Site Lead", value: 98 },
        { name: "Naledi M.", role: "Apprentice", value: 95 },
        { name: "Pieter L.", role: "Field Tech", value: 87 },
        { name: "Johan V.", role: "Mechanic", value: 81 },
      ],
    },
  ];

  const active = metrics[metric];
  const max = Math.max(...active.bars.map((b) => b.value));

  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8 overflow-hidden" data-testid="leaderboard-visual">
      {/* Tools tab (mirrors real Analytics Hub) */}
      <div className="flex items-center gap-1 p-1 bg-white/[0.03] rounded-lg border border-white/[0.05] mb-4 w-fit">
        {[
          { k: "sales", label: "Sales" },
          { k: "field", label: "Field Service", active: true },
          { k: "support", label: "Support" },
          { k: "admin", label: "Admin" },
        ].map((t) => (
          <span
            key={t.k}
            className={`text-[10px] font-mono px-2.5 py-1 rounded transition-colors ${
              t.active ? "bg-rogue-cyan/15 text-rogue-cyan" : "text-geminii-dim"
            }`}
          >
            {t.label}
          </span>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <motion.div
              className="w-1.5 h-1.5 rounded-full bg-rogue-cyan"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono text-rogue-cyan uppercase tracking-wider">Analytics Hub · Leaderboard</span>
          </div>
          <p className="text-xs text-geminii-dim">Pick the metric that matters to your business.</p>
        </div>
        {/* Range pills — mirror real /api/analytics/hub?range=… */}
        <div className="flex items-center gap-1 p-1 bg-white/[0.03] rounded-lg border border-white/[0.05]">
          {[
            { v: "7d", label: "7 days" },
            { v: "30d", label: "30 days" },
            { v: "90d", label: "90 days" },
            { v: "180d", label: "6 months" },
            { v: "365d", label: "1 year" },
          ].map((r) => (
            <button
              key={r.v}
              onClick={() => setRange(r.v)}
              data-testid={`leaderboard-range-${r.v}`}
              className={`text-[10px] font-mono px-2 py-1 rounded transition-colors ${
                range === r.v ? "bg-rogue-cyan/15 text-rogue-cyan" : "text-geminii-dim hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Metric selector chips */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {metrics.map((m, i) => (
          <button
            key={m.key}
            onClick={() => setMetric(i)}
            data-testid={`leaderboard-metric-${m.key}`}
            className={`text-[10px] font-medium px-2.5 py-1 rounded-full border transition-all ${
              i === metric
                ? "bg-rogue-cyan/10 border-rogue-cyan/30 text-rogue-cyan"
                : "bg-white/[0.02] border-white/[0.06] text-geminii-muted hover:text-white hover:border-white/[0.12]"
            }`}
          >
            {m.label}
          </button>
        ))}
        <span className="text-[10px] font-mono text-geminii-dim ml-1">+ custom</span>
      </div>

      {/* HBar list — re-animates when metric or range changes */}
      <div className="space-y-2" key={`${active.key}-${range}`}>
        {active.bars.map((b, i) => (
          <div
            key={`${active.key}-${b.name}`}
            className={`grid grid-cols-[24px_120px_1fr_60px] gap-3 items-center p-2 rounded-lg ${
              i === 0 ? "bg-rogue-cyan/[0.06] border border-rogue-cyan/20" : "bg-white/[0.04]"
            }`}
          >
            <span className={`font-display font-bold text-sm ${i === 0 ? "text-rogue-cyan" : "text-white/85"}`}>
              {i + 1}
            </span>
            <div>
              <p className="text-xs text-white truncate">{b.name}</p>
              <p className="text-[9px] text-geminii-muted truncate">{b.role}</p>
            </div>
            <div className="relative h-5 bg-white/[0.06] rounded overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(b.value / max) * 100}%` }}
                transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: "easeOut" }}
                className={`absolute top-0 left-0 h-full rounded ${
                  i === 0
                    ? "bg-gradient-to-r from-rogue-cyan/55 to-rogue-cyan/90"
                    : i === 1
                    ? "bg-gradient-to-r from-rogue-cyan/35 to-rogue-cyan/70"
                    : i === 2
                    ? "bg-gradient-to-r from-rogue-cyan/25 to-rogue-cyan/50"
                    : "bg-white/[0.22]"
                }`}
              />
            </div>
            <span className="text-[11px] font-mono text-white text-right">
              {b.value}{active.unit}
            </span>
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-5 pt-4 border-t border-white/[0.06] flex items-start gap-2"
      >
        <div className="w-1 self-stretch rounded-full bg-cerebrum-violet/40 flex-shrink-0" />
        <p className="text-[11px] text-geminii-muted leading-relaxed">
          <span className="text-cerebrum-violet font-medium">Same widget</span> powers every analytics tab —
          Sales (rep leaderboard), Field (technician utilisation, photo compliance, customer ratings),
          Support (resolution speed), and Teams. Owner / admin / manager / rep gated.
        </p>
      </motion.div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Teams — CV Extraction & Role Matching                            */
/* ---------------------------------------------------------------- */
export function CVExtractionVisual() {
  const skills = [
    { label: "Electrical install", match: 94 },
    { label: "Solar PV certified", match: 88 },
    { label: "Site management", match: 71 },
    { label: "First-aid (Level 2)", match: 100 },
    { label: "Driver's licence (Code 10)", match: 100 },
  ];

  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8 overflow-hidden" data-testid="cv-extraction-visual">
      <div className="flex items-center gap-2 mb-5">
        <span className="text-[10px] font-mono text-cerebrum-violet uppercase tracking-wider">Cerebrum · CV Extraction</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-5 items-start">
        {/* "CV" document */}
        <div className="relative w-full md:w-[120px] aspect-[3/4] bg-white/[0.03] border border-white/[0.08] rounded-md p-3 flex-shrink-0">
          <div className="space-y-1.5">
            <div className="h-1.5 bg-white/[0.18] rounded w-3/4" />
            <div className="h-1 bg-white/[0.08] rounded w-1/2" />
            <div className="h-px bg-white/[0.06] my-2" />
            <div className="h-1 bg-white/[0.06] rounded w-full" />
            <div className="h-1 bg-white/[0.06] rounded w-5/6" />
            <div className="h-1 bg-white/[0.06] rounded w-2/3" />
            <div className="h-px bg-white/[0.06] my-2" />
            <div className="h-1 bg-white/[0.06] rounded w-3/4" />
            <div className="h-1 bg-white/[0.06] rounded w-1/2" />
            <div className="h-1 bg-white/[0.06] rounded w-2/3" />
          </div>
          <motion.div
            className="absolute left-2 right-2 h-0.5 bg-cerebrum-violet/70 rounded"
            animate={{ top: ["8%", "92%", "8%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[9px] font-mono text-geminii-dim bg-geminii-card px-1">
            CV.pdf
          </span>
        </div>

        {/* Extracted data */}
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-mono text-geminii-dim mb-1">CANDIDATE</p>
            <p className="text-sm text-white">Tshepo Mokoena · Senior Field Technician</p>
          </div>
          <div>
            <p className="text-[10px] font-mono text-geminii-dim mb-2">ROLE-MATCH SCORE</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "87%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.4 }}
                  className="h-full bg-gradient-to-r from-cerebrum-violet to-rogue-cyan rounded-full"
                />
              </div>
              <span className="font-display font-bold text-2xl text-cerebrum-violet">87%</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono text-geminii-dim mb-2">SKILL FIT</p>
            <div className="space-y-1.5">
              {skills.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + i * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <span className="text-[11px] text-geminii-muted flex-1 truncate">{s.label}</span>
                  <div className="w-20 h-1 bg-white/[0.04] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${s.match}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.9 + i * 0.1, duration: 0.6 }}
                      className={`h-full rounded-full ${
                        s.match >= 90 ? "bg-rogue-emerald" : s.match >= 75 ? "bg-rogue-cyan" : "bg-rogue-amber"
                      }`}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-white w-8 text-right">{s.match}%</span>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.6 }}
            className="text-[11px] text-rogue-emerald pt-2 border-t border-white/[0.06]"
          >
            Recommended for interview. 3 similar roles filled successfully.
          </motion.p>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Ops — Job Card Completion Flow                                   */
/* Anchored in the canonical Rogue JC row (short_id · PhaseChip ·   */
/* JCProgressBar) so the marketing flow matches the actual app.     */
/* ---------------------------------------------------------------- */
export function JobCardFlowVisual() {
  const steps = [
    { label: "Clock-in", detail: "GPS verified · 07:42", state: "done", color: "bg-rogue-emerald" },
    { label: "Arrive on site", detail: "Acacia Farms · Site 14", state: "done", color: "bg-rogue-emerald" },
    { label: "Photo: Before", detail: "3 photos captured", state: "done", color: "bg-rogue-emerald" },
    { label: "Checklist", detail: "8 of 8 items checked", state: "done", color: "bg-rogue-emerald" },
    { label: "Photo: After", detail: "Capturing…", state: "active", color: "bg-rogue-cyan" },
    { label: "Customer signature", detail: "Pending", state: "pending", color: "bg-white/[0.1]" },
    { label: "Submit & sync", detail: "Auto-invoice ready", state: "pending", color: "bg-white/[0.1]" },
  ];
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8 overflow-hidden" data-testid="jobcard-flow-visual">
      {/* Canonical JC Row header — same preview schema used everywhere in Rogue */}
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-lg p-3 mb-5">
        <div className="flex items-start gap-3">
          <span className="font-mono text-[10px] text-rogue-cyan mt-0.5">#A1B2C3</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">Pump 14 — pressure-drop service</p>
            <p className="text-[11px] text-geminii-muted truncate">
              <span className="text-rogue-cyan/80">Acacia Farms</span> · Site 14 · 14/02/2026 07:30
            </p>
          </div>
          {/* PhaseChip — real component used app-wide */}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rogue-cyan/15 border border-rogue-cyan/25 text-[10px] font-mono text-rogue-cyan whitespace-nowrap">
            <span className="w-1 h-1 rounded-full bg-rogue-cyan" />
            in_progress
          </span>
        </div>
        {/* JCProgressBar — also real */}
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "57%" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full bg-rogue-cyan rounded-full"
            />
          </div>
          <span className="text-[9px] font-mono text-geminii-dim">4 / 7</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">Completion flow · mobile</p>
        <p className="text-[10px] font-mono text-rogue-cyan">Tshepo M. · Field Tech</p>
      </div>

      <div className="relative pl-6">
        {/* Timeline line */}
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/[0.08]" />

        {steps.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 + i * 0.12 }}
            className="relative flex items-center gap-4 py-2"
          >
            <div className={`absolute -left-6 w-3.5 h-3.5 rounded-full ${s.color} border-2 border-geminii-card flex items-center justify-center`}>
              {s.state === "active" && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-rogue-cyan"
                  animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
              )}
            </div>
            <div className="flex-1 grid grid-cols-[1fr_auto] gap-2 items-center">
              <span
                className={`text-xs ${
                  s.state === "done" ? "text-white/85" : s.state === "active" ? "text-white" : "text-geminii-dim"
                }`}
              >
                {s.label}
              </span>
              <span className="text-[10px] font-mono text-geminii-dim">{s.detail}</span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between">
        <span className="text-[10px] font-mono text-rogue-cyan">Efficiency: 22 min faster than avg</span>
        <span className="text-[10px] font-mono text-geminii-dim">Offline-safe · UploadDock-managed</span>
      </div>
    </div>
  );
}


/* ---------------------------------------------------------------- */
/* Teams — Badges & Rewards (gamified recognition)                  */
/* ---------------------------------------------------------------- */
export function BadgesRewardsVisual() {
  const awards = [
    {
      tag: "Early Bird of the Week",
      person: "Marcus T.",
      detail: "5 clock-ins before 07:00",
      icon: "🌅",
      tint: "bg-rogue-amber/10 border-rogue-amber/25",
      iconBg: "bg-rogue-amber/15",
    },
    {
      tag: "Perfect Attendance",
      person: "Sarah K.",
      detail: "28-day on-time streak",
      icon: "🎯",
      tint: "bg-rogue-cyan/10 border-rogue-cyan/25",
      iconBg: "bg-rogue-cyan/15",
    },
    {
      tag: "Most Improved",
      person: "Naledi M.",
      detail: "+18% on-time vs last month",
      icon: "📈",
      tint: "bg-rogue-emerald/10 border-rogue-emerald/25",
      iconBg: "bg-rogue-emerald/15",
    },
    {
      tag: "Helping Hand",
      person: "Johan V.",
      detail: "Covered 4 shifts for teammates",
      icon: "🤝",
      tint: "bg-cerebrum-violet/10 border-cerebrum-violet/25",
      iconBg: "bg-cerebrum-violet/15",
    },
    {
      tag: "Gentle Nudge",
      person: "Manager-only",
      detail: "1 person flagged · coaching prompts ready",
      icon: "🐢",
      tint: "bg-rogue-rose/10 border-rogue-rose/25",
      iconBg: "bg-rogue-rose/15",
    },
    {
      tag: "Cerebrum's Pick",
      person: "Marcus T.",
      detail: "Job quality + speed top 5%",
      icon: "🏆",
      tint: "bg-rogue-amber/10 border-rogue-amber/30",
      iconBg: "bg-rogue-amber/20",
    },
  ];

  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8 overflow-hidden" data-testid="badges-rewards-visual">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <motion.div
              className="w-1.5 h-1.5 rounded-full bg-rogue-amber"
              animate={{ scale: [1, 1.6, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono text-rogue-amber uppercase tracking-wider">Badges & Recognition</span>
          </div>
          <p className="text-xs text-geminii-dim">Auto-awarded weekly · published to team chat</p>
        </div>
        <span className="text-[10px] font-mono text-geminii-dim">Week 7</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {awards.map((a, i) => (
          <motion.div
            key={a.tag}
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 + i * 0.1, type: "spring", stiffness: 180, damping: 18 }}
            whileHover={{ y: -2 }}
            className={`relative flex items-center gap-3 p-3 rounded-xl border ${a.tint}`}
          >
            <div className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0 text-lg`}>
              <span>{a.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider truncate">{a.tag}</p>
              <p className="text-sm text-white truncate">{a.person}</p>
              <p className="text-[10px] text-geminii-muted truncate">{a.detail}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.4 }}
        className="mt-5 pt-4 border-t border-white/[0.06] flex items-start gap-2"
      >
        <div className="w-1 h-full min-h-[28px] rounded-full bg-cerebrum-violet/40 flex-shrink-0" />
        <p className="text-[11px] text-geminii-muted leading-relaxed">
          <span className="text-cerebrum-violet font-medium">Cerebrum: </span>
          Recognition lifts retention more than raises. Auto-awarded badges go straight to team chat —
          no manager needed to remember who deserves a shout-out.
        </p>
      </motion.div>
    </div>
  );
}
